import { useCallback, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

/**
 * The shape of a support / app link and "Install app" (spec 2026-10-08-kiosk-web-split §5). Kept
 * apart from supportLinks.tsx (Telegram, Android, the tour) so Me can offer Install app without
 * the client links env or the tour.
 */

export type SupportLinkId = 'install' | 'askUs' | 'telegram' | 'android' | 'iphoneGuide' | 'tour';

export interface SupportLink {
  id: SupportLinkId;
  /** The word (translated): button label, tooltip, row title. */
  label: string;
  /** A short hint (translated): row subtitle. */
  hint: string;
  icon: React.ReactNode;
  /** Opens another site in a new window. */
  external: boolean;
  onSelect: () => void;
}

/** The iPhone Home Screen guide (IosPwaGuideModal) the caller renders. */
export interface IosGuideState {
  open: boolean;
  onClose: () => void;
}

export function useIosGuide() {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);
  return { state: { open, onClose } as IosGuideState, show };
}

/**
 * "Install app": the browser's install prompt where there is one, the Home Screen guide on iOS.
 * Null when there is nothing to offer (kiosk or unknown surface, installed, not installable).
 */
export function useInstallLink(showIosGuide: () => void): SupportLink | null {
  const { t } = useTranslation();
  const { canInstall, isIos, promptInstall } = useInstallPrompt();
  if (!canInstall && !isIos) return null;
  return {
    id: 'install',
    label: t('install.title'),
    hint: t('install.hint'),
    icon: <Download />,
    external: false,
    onSelect: () => {
      if (isIos) showIosGuide();
      else void promptInstall();
    },
  };
}

/** Me's "Install app" row: the link (or null) and the guide it may open. */
export function useInstallAppLink(): { link: SupportLink | null; iosGuide: IosGuideState } {
  const guide = useIosGuide();
  return { link: useInstallLink(guide.show), iosGuide: guide.state };
}
